import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardBody } from "../components/index.js";
import { useAuth } from "../context/AuthContext.jsx";
import { apiFetch } from "../lib/api.js";

/**
 * My Profile — the logged-in user's *own* account.
 *
 * Deliberately separate from "Client Settings" (the Account page), which shows
 * whichever client is currently in view. This page is always about you: your
 * name, role, password, and sign-out — none of which change when an admin
 * switches the viewed client via ?clientId.
 */
export function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Deliberate sign-out: clear the session AND land on a clean /login. Without
  // the explicit navigate we'd stay on /profile while unauthenticated, so
  // ProtectedRoute would stash `from: /profile` — and the next sign-in would
  // bounce back here instead of the role-aware home (HomeRedirect → /agency for
  // admins). Going straight to /login carries no `from`, so login falls through
  // to HomeRedirect. (Session-expiry logout via the 401 path is untouched and
  // still resumes the page the user was on.)
  function handleSignOut() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="space-y-5 max-w-[1000px]">
      <Card>
        <CardHeader
          title="My Profile"
          subtitle="Your identity on Prism"
          right={
            <button
              type="button"
              onClick={handleSignOut}
              className="h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:bg-[var(--color-border-light)] transition-colors"
            >
              Sign out
            </button>
          }
        />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Name"  value={user?.name} />
            <Field label="Email" value={user?.email} />
            <Field label="Role"  value={roleLabel(user?.role)} />
            {user?.jobTitle && <Field label="Job Title" value={user.jobTitle} />}
          </div>
        </CardBody>
      </Card>

      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState({ busy: false, msg: null, error: null });

  async function submit(e) {
    e.preventDefault();
    setState({ busy: true, msg: null, error: null });
    if (next.length < 8) {
      setState({ busy: false, msg: null, error: "New password must be at least 8 characters" });
      return;
    }
    if (next !== confirm) {
      setState({ busy: false, msg: null, error: "New passwords don't match" });
      return;
    }
    try {
      await apiFetch("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      setState({ busy: false, msg: "Password updated.", error: null });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setState({ busy: false, msg: null, error: e.message });
    }
  }

  return (
    <Card>
      <CardHeader title="Change Password" subtitle="Updates take effect immediately" />
      <CardBody>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <PasswordField label="Current" value={current} onChange={setCurrent} autoComplete="current-password" />
          <PasswordField label="New"     value={next}    onChange={setNext}    autoComplete="new-password" help="Min 8 chars" />
          <PasswordField label="Confirm" value={confirm} onChange={setConfirm} autoComplete="new-password" />
          <div className="md:col-span-3 flex items-center justify-between gap-4">
            <div className="text-[12px]">
              {state.msg   && <span className="text-positive">{state.msg}</span>}
              {state.error && <span className="text-negative">{state.error}</span>}
            </div>
            <button
              type="submit" disabled={state.busy || !current || !next || !confirm}
              className="h-9 px-4 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state.busy ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function PasswordField({ label, value, onChange, autoComplete, help }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>
        {label}
      </div>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="w-full h-9 px-3 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors"
      />
      {help && <div className="text-[10px] text-text-muted mt-1">{help}</div>}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase text-text-muted font-medium mb-1"
        style={{ letterSpacing: "0.5px" }}
      >
        {label}
      </div>
      <div className="text-[13px] text-text-primary">{value || "—"}</div>
    </div>
  );
}

function roleLabel(role) {
  const labels = {
    SUPER_ADMIN: "Super Admin",
    ADMIN: "Admin",
    EMPLOYEE: "Employee",
    CLIENT: "Client",
    OWNER: "Owner",
    SALES: "Sales",
  };
  return labels[role] || role;
}
